import { HEARTBEAT_TIMEOUT_MS, ONLINE_PROBE_TIMEOUT_MS, type ChannelStatus, type FrameMeta, type JoinOptions, type RealtimeChannel, type RealtimeTransport } from "./types";

/**
 * Cloudflare Durable Object realtime transport — the ONE socket manager every
 * surface (web, desktop, mobile) shares via getRealtimeTransport().
 *
 * Plain `WebSocket` + `setTimeout` + `JSON` only — no DOM/Vite/Node APIs
 * beyond an optional global `online` listener — so the exact same class runs
 * under Vite (web/desktop) and Metro/Hermes (React Native).
 */
interface DoTransportOptions {
  baseUrl: string; // wss://rt.slumbr.xyz
  getToken: (scope: string) => Promise<string>;
}

interface PresenceMsg {
  type: "presence.snapshot" | "presence.join" | "presence.leave";
  users?: string[];
  userId?: string;
}

/** Cap on events buffered while the socket is down — oldest are dropped.
 *  Consumers refetch on reconnect anyway; an unbounded queue just replays a
 *  flood of stale publishes after a long offline stretch. */
const MAX_PUBLISH_QUEUE = 50;

/** Close code the realtime worker sends when a newer socket for the same user
 *  replaced this one (game rooms allow one socket per user). Reconnecting would
 *  just kick the newer socket in turn — two tabs flapping forever — so it is
 *  terminal for the channel. */
export const CLOSE_SUPERSEDED = 4001;

/** Subprotocol the realtime worker echoes back on a successful upgrade. The
 *  JWT rides as a second offered protocol, `auth.<jwt>`, so it stays out of
 *  URLs (and therefore out of proxy/CDN access logs). */
export const RT_SUBPROTOCOL = "slumbr.rt";
/** RFC 6455 subprotocol values must be RFC 7230 tokens; `new WebSocket` throws
 *  a SyntaxError on anything else. JWTs (base64url + '.') always match. */
const SUBPROTOCOL_TOKEN = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;

export function buildSubprotocols(token: string): string[] | undefined {
  return SUBPROTOCOL_TOKEN.test(token) ? [RT_SUBPROTOCOL, `auth.${token}`] : undefined;
}

/** Whether a received frame was relayed by another client (worker-stamped
 *  `srvFrom`) rather than broadcast by the server. */
export function isPeerFrame(event: unknown): boolean {
  return frameMeta(event).fromPeer;
}

function frameMeta(event: unknown): FrameMeta {
  const srvFrom =
    event && typeof event === "object" ? (event as { srvFrom?: unknown }).srvFrom : undefined;
  return typeof srvFrom === "string" && srvFrom.length > 0
    ? { fromPeer: true, srvFrom }
    : { fromPeer: false };
}

interface OnlineEventTarget {
  addEventListener?: (type: "online" | "offline", listener: () => void) => void;
  removeEventListener?: (type: "online" | "offline", listener: () => void) => void;
}

interface VisibilityTarget {
  visibilityState?: string;
  addEventListener?: (type: "visibilitychange", listener: () => void) => void;
  removeEventListener?: (type: "visibilitychange", listener: () => void) => void;
}

/** Browsers expose `document` visibility events; RN/Hermes don't. */
function visibilityTarget(): VisibilityTarget | null {
  const d = (globalThis as { document?: VisibilityTarget }).document;
  return d && typeof d.addEventListener === "function" && typeof d.removeEventListener === "function" ? d : null;
}

/** Browsers expose `online` on the global (window); RN/Hermes may not. */
function onlineTarget(): OnlineEventTarget | null {
  const g = globalThis as OnlineEventTarget;
  return typeof g.addEventListener === "function" && typeof g.removeEventListener === "function"
    ? g
    : null;
}

class DoChannel implements RealtimeChannel {
  private ws: WebSocket | null = null;
  private handlers = new Set<(event: unknown, meta: FrameMeta) => void>();
  private reconnectHandlers = new Set<() => void>();
  private supersededHandlers = new Set<() => void>();
  private statusHandlers = new Set<(status: ChannelStatus) => void>();
  /** A newer connection for this user took over (close 4001): never reconnect. */
  private superseded = false;
  private online = new Set<string>();
  private closed = false;
  private backoff = 500;
  private queue: string[] = [];
  private everConnected = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** Heartbeat (opts.heartbeatMs): when the current socket last received anything. */
  private lastRx = 0;
  private heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  private probeTimer: ReturnType<typeof setTimeout> | null = null;
  private onNetworkOnline = () => {
    // The OS just told us connectivity is back — skip the remaining backoff
    // and reconnect immediately if we're currently down.
    if (this.closed || this.superseded) return;
    if (this.ws) {
      // A socket we still believe open may be half-open after the outage / network switch: probe it
      // now with the short timeout (restarting any probe the `offline` event left in flight).
      if (this.opts.heartbeatMs && this.ws.readyState === WebSocket.OPEN) this.probe(this.ws, ONLINE_PROBE_TIMEOUT_MS, true);
      return;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.backoff = 500;
    void this.connect();
  };

  /**
   * Heartbeat channels only: the OS says the network is gone. Browsers don't close established
   * sockets on `offline` (a Wi-Fi drop leaves them half-open), so probe: a socket that stays silent
   * for HEARTBEAT_TIMEOUT_MS is dropped (and reconnects at once on `online`); one that answers — the
   * outage was a blip — is kept.
   */
  private onNetworkOffline = () => {
    if (this.closed || this.superseded || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.probe(this.ws);
  };

  /** Back in the foreground (timers were throttled while hidden, the network may have changed): probe. */
  private onVisibility = () => {
    if (this.closed || this.superseded || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    if (visibilityTarget()?.visibilityState === "visible") this.probe(this.ws);
  };

  constructor(
    private room: string,
    private baseUrl: string,
    private getToken: (scope: string) => Promise<string>,
    private opts: JoinOptions = {}
  ) {
    onlineTarget()?.addEventListener?.("online", this.onNetworkOnline);
    if (opts.heartbeatMs) {
      onlineTarget()?.addEventListener?.("offline", this.onNetworkOffline);
      visibilityTarget()?.addEventListener?.("visibilitychange", this.onVisibility);
    }
    void this.connect();
  }

  /** Stop listening to the environment (terminal: closed / superseded). */
  private detachGlobalListeners(): void {
    onlineTarget()?.removeEventListener?.("online", this.onNetworkOnline);
    if (this.opts.heartbeatMs) {
      onlineTarget()?.removeEventListener?.("offline", this.onNetworkOffline);
      visibilityTarget()?.removeEventListener?.("visibilitychange", this.onVisibility);
    }
  }

  private async connect(): Promise<void> {
    if (this.closed || this.superseded) return;
    this.emitStatus({ state: "connecting" });
    let token: string;
    try {
      token = await this.getToken(this.room.replace("/", ":"));
    } catch {
      if (this.closed || this.superseded) return;
      this.emitStatus({ state: "down", code: null });
      this.scheduleReconnect();
      return;
    }
    if (this.closed || this.superseded || this.ws) return;
    // TODO(remove after 2026-10-12): drop the `?token=` query once every
    // realtime worker in the fleet accepts subprotocol auth (worker W1).
    const url = `${this.baseUrl}/${this.room}?token=${encodeURIComponent(token)}`;
    const protocols = buildSubprotocols(token);
    const ws = protocols ? new WebSocket(url, protocols) : new WebSocket(url);
    this.ws = ws;
    ws.onopen = () => {
      this.backoff = 500;
      this.startHeartbeat(ws);
      // Status first: a handshake consumer (queueWhileDown: false) sends its
      // hello from here, ahead of anything else on the new socket.
      this.emitStatus({ state: "open" });
      for (const m of this.queue.splice(0)) ws.send(m);
      if (this.everConnected) {
        // Anything broadcast while we were down is gone — let consumers
        // refetch the state they mirror off this channel.
        for (const h of this.reconnectHandlers) h();
      }
      this.everConnected = true;
    };
    ws.onmessage = (e: MessageEvent) => {
      this.lastRx = Date.now(); // any frame (the DO's bare "pong" included) proves the socket alive
      let msg: PresenceMsg;
      try {
        msg = JSON.parse(e.data as string);
      } catch {
        return;
      }
      const meta = frameMeta(msg);
      // Presence is server-authored; a relayed look-alike must not rewrite it.
      if (!meta.fromPeer) this.applyPresence(msg);
      for (const h of this.handlers) h(msg, meta);
    };
    ws.onclose = (e?: CloseEvent) => {
      const current = this.ws === ws;
      if (current) {
        this.ws = null;
        this.stopHeartbeat();
      }
      if (this.closed || this.superseded) return;
      if (e?.code === CLOSE_SUPERSEDED) {
        // Only the live socket being replaced is terminal; a stale socket's
        // late 4001 (our own reconnect replacing it) must not kill the channel.
        if (current) this.markSuperseded();
        return;
      }
      if (!current) return; // a stale socket closing must not schedule a second reconnect
      if (e?.code !== undefined && this.opts.terminalCloseCodes?.includes(e.code)) {
        this.closed = true;
        this.queue = [];
        this.detachGlobalListeners();
        this.emitStatus({ state: "closed", code: e.code });
        return;
      }
      this.emitStatus({ state: "down", code: e?.code ?? null });
      this.scheduleReconnect();
    };
    ws.onerror = () => {
      try {
        ws.close();
      } catch {
        /* noop */
      }
    };
  }

  /** Heartbeat: every `heartbeatMs`, a socket that has been quiet that long is probed. */
  private startHeartbeat(ws: WebSocket): void {
    const every = this.opts.heartbeatMs;
    if (!every) return;
    this.stopHeartbeat();
    this.lastRx = Date.now();
    const tick = () => {
      this.heartbeatTimer = null;
      if (this.ws !== ws || this.closed || this.superseded) return;
      if (Date.now() - this.lastRx >= every) this.probe(ws);
      this.heartbeatTimer = setTimeout(tick, every);
    };
    this.heartbeatTimer = setTimeout(tick, every);
  }

  /**
   * Send the DO's auto-answered "ping"; nothing back within `timeoutMs` → the socket is dead.
   * `restart`: replace a probe already in flight (the `online` probe must not wait out an older,
   * longer one sent while the network was down).
   */
  private probe(ws: WebSocket, timeoutMs = HEARTBEAT_TIMEOUT_MS, restart = false): void {
    if (this.probeTimer) {
      if (!restart) return; // one probe in flight is enough
      clearTimeout(this.probeTimer);
      this.probeTimer = null;
    }
    const sentAt = Date.now();
    try {
      ws.send("ping");
    } catch {
      /* a send on a dying socket: the timeout below settles it */
    }
    this.probeTimer = setTimeout(() => {
      this.probeTimer = null;
      if (this.ws === ws && this.lastRx < sentAt) this.dropDeadSocket(ws);
    }, timeoutMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) clearTimeout(this.heartbeatTimer);
    if (this.probeTimer) clearTimeout(this.probeTimer);
    this.heartbeatTimer = null;
    this.probeTimer = null;
  }

  /** A half-open socket: forget it now (its `close` may take minutes) and reconnect as after a drop. */
  private dropDeadSocket(ws: WebSocket): void {
    if (this.ws !== ws || this.closed || this.superseded) return;
    this.stopHeartbeat();
    this.ws = null;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onclose = null;
    ws.onerror = null;
    try {
      ws.close();
    } catch {
      /* noop */
    }
    this.emitStatus({ state: "down", code: null });
    this.backoff = 500;
    this.scheduleReconnect();
  }

  private applyPresence(msg: PresenceMsg): void {
    if (msg.type === "presence.snapshot" && msg.users) {
      this.online = new Set(msg.users);
    } else if (msg.type === "presence.join" && msg.userId) {
      this.online.add(msg.userId);
    } else if (msg.type === "presence.leave" && msg.userId) {
      this.online.delete(msg.userId);
    }
  }

  private markSuperseded(): void {
    this.superseded = true;
    this.stopHeartbeat();
    this.queue = [];
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.detachGlobalListeners();
    this.emitStatus({ state: "superseded" });
    for (const h of [...this.supersededHandlers]) h();
  }

  private emitStatus(status: ChannelStatus): void {
    for (const h of [...this.statusHandlers]) h(status);
  }

  private scheduleReconnect(): void {
    const delay = Math.min(this.backoff, 15_000);
    this.backoff = Math.min(this.backoff * 2, 15_000);
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, delay);
  }

  publish(event: unknown): void {
    // Nothing will ever flush the queue of a superseded channel.
    if (this.superseded) return;
    const data = JSON.stringify(event);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data);
    } else {
      if (this.opts.queueWhileDown === false) return;
      this.queue.push(data);
      if (this.queue.length > MAX_PUBLISH_QUEUE) this.queue.shift();
    }
  }
  on(handler: (event: unknown, meta: FrameMeta) => void): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }
  onReconnect(handler: () => void): () => void {
    this.reconnectHandlers.add(handler);
    return () => this.reconnectHandlers.delete(handler);
  }
  onSuperseded(handler: () => void): () => void {
    if (this.superseded) {
      handler();
      return () => {};
    }
    this.supersededHandlers.add(handler);
    return () => this.supersededHandlers.delete(handler);
  }
  isSuperseded(): boolean {
    return this.superseded;
  }
  onStatus(handler: (status: ChannelStatus) => void): () => void {
    this.statusHandlers.add(handler);
    return () => this.statusHandlers.delete(handler);
  }
  presence(): ReadonlySet<string> {
    return this.online;
  }
  close(): void {
    this.closed = true;
    this.handlers.clear();
    this.reconnectHandlers.clear();
    this.supersededHandlers.clear();
    this.statusHandlers.clear();
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.detachGlobalListeners();
    try {
      this.ws?.close();
    } catch {
      /* noop */
    }
  }
}

export class DurableObjectTransport implements RealtimeTransport {
  constructor(private opts: DoTransportOptions) {}
  join(room: string, opts?: JoinOptions): RealtimeChannel {
    return new DoChannel(room, this.opts.baseUrl, this.opts.getToken, opts);
  }
}
